/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {useEffect, useState, useCallback} from "react";
import {useTypedSelector} from "helpers/reducers";
import {useDispatch} from "useDispatch";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";
import {AVAILABLE_DURATIONS_BASIC, AVAILABLE_DURATIONS_EVENTS, DEFAULT_DURATION, eventCategories} from "helpers/config";
import Dates from "utils/Dates";
import {usePackageItemKartingOptions} from "utils/packageUtils";
import BookingAction from "store/booking/BookingAction";
import {PackagesItemsPerson} from "types/entities/PackagesItems";

export const useBookingPackagesRowInitialization = (personIndex?: number, personType: PackageItemPersonType = PackageItemPersonType.Adult) => {
    const dispatch = useDispatch();
    const availablePackageItems = useTypedSelector(state => state.bookingState.bookingProcess.availablePackageItems);
    const selectedPackageItems = useTypedSelector(state => state.bookingState.bookingProcess.selectedPackageItems);
    const selectedPackageCreditItems = useTypedSelector(state => state.bookingState.bookingProcess.selectedPackageCreditItems);
    const selectedTimeslot = useTypedSelector(state => state.bookingState.bookingProcess.selectedTimeslot);

    const {getPackageItemByDuration, setPackageItemHandler} = usePackageItemKartingOptions();

    const filter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const isEvent = filter.category ? eventCategories.includes(filter.category) : false;
    const durations = isEvent ? AVAILABLE_DURATIONS_EVENTS : AVAILABLE_DURATIONS_BASIC;

    const [chosenDuration, setChosenDuration] = useState<number | undefined>(DEFAULT_DURATION);
    const [selectedPackageItem, setSelectedPackageItem] = useState<PackagesItemsPerson | null>(null);

    const findClosestDuration = useCallback((preferred: number | undefined, pType: PackageItemPersonType) => {
        const priority = preferred && durations.includes(preferred) ? preferred : DEFAULT_DURATION;
        const order = [priority, ...durations.filter(d => d !== priority)];
        return order.find(d => {
            const item = getPackageItemByDuration(d, pType);
            return item && (d !== 60 || Dates.hour(selectedTimeslot?.start_at) >= 18);
        });
    }, [getPackageItemByDuration, selectedTimeslot, durations]);

    const handleDurationChange = useCallback((newDuration: number) => {
        const duration = findClosestDuration(newDuration, personIndex !== undefined ? personType : PackageItemPersonType.Adult);
        if (duration !== undefined) {
            setChosenDuration(duration);
            if (personIndex !== undefined) {
                const packageItem = getPackageItemByDuration(duration, personType);
                if (packageItem) {
                    setPackageItemHandler(personIndex, packageItem.id, personType, 1);
                    setSelectedPackageItem(packageItem);
                }
            } else {
                const newSelectedPackageItems = selectedPackageItems.map(item => {
                    const newTicket = getPackageItemByDuration(duration, item.person_type);
                    return {...item, id: newTicket?.id ?? null};
                });
                dispatch(BookingAction.setSelectedPackageItems(newSelectedPackageItems));
            }
        }
    }, [findClosestDuration, personIndex, personType, getPackageItemByDuration, setPackageItemHandler, selectedPackageItems, dispatch]);

    useEffect(() => {
        if (!availablePackageItems) return;

        if (personIndex !== undefined) {
            // Per-person logic (Standard row)
            const itemByPerson = selectedPackageItems.find(item => item.personIndex === personIndex);
            const isInitialized = itemByPerson && itemByPerson.id !== null;

            if (!isInitialized) {
                const duration = findClosestDuration(chosenDuration, personType);
                if (duration !== undefined) {
                    setChosenDuration(duration);
                    const packageItem = getPackageItemByDuration(duration, personType);
                    if (packageItem) {
                        setPackageItemHandler(personIndex, packageItem.id, personType, 1);
                        setSelectedPackageItem(packageItem);
                    }
                }
            } else {
                // Sync local chosenDuration and selectedPackageItem with selected item in Redux
                const packageItem = availablePackageItems.packageItems[itemByPerson.person_type]?.find(i => i.id === itemByPerson.id);
                if (packageItem) {
                    if (packageItem.duration !== chosenDuration) {
                        setChosenDuration(packageItem.duration);
                    }
                    setSelectedPackageItem(packageItem);
                }
            }
        } else {
            // Global logic (Events / Family row)
            const isInitialized = selectedPackageCreditItems.length > 0 || selectedPackageItems.some(item => item.id !== null);

            if (isInitialized) {
                if (selectedPackageItems.length > 0) {
                    const firstSelected = selectedPackageItems[0];
                    const allItems = [
                        ...(availablePackageItems.packageItems[PackageItemPersonType.Adult] || []),
                        ...(availablePackageItems.packageItems[PackageItemPersonType.Child] || []),
                    ];
                    const found = allItems.find(item => item.id === firstSelected.id);
                    if (found && found.duration) {
                        setChosenDuration(found.duration);
                    }
                }
            } else {
                const duration = findClosestDuration(chosenDuration, PackageItemPersonType.Adult);
                if (duration !== undefined) {
                    setChosenDuration(duration);
                    const newSelectedPackageItems = selectedPackageItems.map(item => {
                        const newTicket = getPackageItemByDuration(duration, item.person_type);
                        return {...item, id: newTicket?.id ?? null};
                    });
                    dispatch(BookingAction.setSelectedPackageItems(newSelectedPackageItems));
                }
            }
        }
    }, [availablePackageItems, selectedPackageItems, selectedPackageCreditItems.length, personIndex, personType, findClosestDuration, getPackageItemByDuration, setPackageItemHandler, dispatch]);

    return {
        chosenDuration,
        setChosenDuration,
        selectedPackageItem,
        handleDurationChange,
    };
};
